import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import '../Shared/Menu.css';
import './Login.css';
import logo from '../../images/audithippo.png';
import usePageTitle from '../Shared/usePageTitle';

const ACTIVATE_API_URL = '/rest/api/v1/admin/users/activate';

function ActiveUser() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [menuOpen, setMenuOpen] = useState(false);
    usePageTitle('Activate Account');

    const invitationId = searchParams.get('invitationcode') || '';
    const email        = searchParams.get('useremail') || '';

    return (
        <>
            {/* Mobile Menu Overlay */}
            <div
                className={`mobile-menu-overlay${menuOpen ? ' show' : ''}`}
                onClick={() => setMenuOpen(false)}
            />

            {/* Mobile Slide Menu */}
            <div className={`mobile-menu${menuOpen ? ' show' : ''}`}>
                <div className="mobile-menu-header">
                    <a href="#" className="logobock d-flex align-items-center gap-2">
                        <div className="hippolog">
                            <img src={logo} alt="AuditHippo" />
                        </div>
                        <span className="brand-name">
                            Audit<span className="hippotxt">Hippo</span>
                        </span>
                    </a>
                    <div
                        className="mobile-menu-close"
                        onClick={() => setMenuOpen(false)}
                        role="button"
                        aria-label="Close menu"
                    >
                        ✕
                    </div>
                </div>
            </div>

            {/* Navbar */}
            <nav className="navbar-custom">
                <div className="container-custom">
                    <div className="d-flex align-items-center justify-content-between w-100 flex-wrap gap-2">
                        <div className="d-flex align-items-center gap-3">
                            <button
                                className="mobile-menu-btn"
                                onClick={() => setMenuOpen(true)}
                                aria-label="Open menu"
                            >
                                <span />
                                <span />
                                <span />
                            </button>
                            <div className="d-flex align-items-center gap-2">
                                <a href="#" className="logobock d-flex align-items-center gap-2">
                                    <div className="hippolog">
                                        <img src={logo} alt="AuditHippo" />
                                    </div>
                                    <span className="brand-name">
                                        Audit<span className="hippotxt">Hippo</span>
                                    </span>
                                </a>
                                <span className="divider-mobile" />
                                <span className="marketing-cloud-text">Marketing Cloud</span>
                            </div>
                        </div>

                        <div className="environment-badge d-flex align-items-center gap-2">
                            <div className="environment-dot" />
                            <span className="environment-text">Account Activation</span>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Main Content */}
            <main className="container-main fade-up" style={{ maxWidth: 1200, margin: '0 auto' }}>
                <div className="page-header mb-4">
                    <h1>Activate Account</h1>
                    <p>Set a password to activate your AuditHippo account</p>
                </div>

                <div
                    className="login-screens-row"
                    style={{ display: 'flex', justifyContent: 'center' }}
                >
                    <div style={{ width: '100%', maxWidth: 520 }}>
                        <p className="login-screen-label">Account Activation</p>

                        <div className="login-card">
                            <div className="card-body">
                                {/* Brand */}
                                <div className="d-flex align-items-center gap-2 mb-4">
                                    <div className="logo-box">AH</div>
                                    <span className="brand-name">AuditHippo</span>
                                </div>

                                <span className="login-badge-user">Activate Account</span>

                                <h2 className="login-card-title">Welcome to AuditHippo</h2>
                                <p className="login-card-subtitle">
                                    {email
                                        ? <>Activating account for <strong>{email}</strong>.</>
                                        : 'Set your password to continue.'}
                                </p>

                                <ActivateForm
                                    invitationId={invitationId}
                                    email={email}
                                    onActivated={() => navigate('/login')}
                                />

                                <p className="login-footer-text mb-0">
                                    Already have an account?{' '}
                                    <a href="#" onClick={(e) => { e.preventDefault(); navigate('/login'); }} className="login-link">Sign in</a>
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </>
    );
}

function ActivateForm({ invitationId, email, onActivated }) {
    const [password, setPassword]               = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError]                     = useState('');
    const [success, setSuccess]                 = useState('');
    const [submitting, setSubmitting]           = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        if (!invitationId || !email) {
            setError('Invalid activation link. Please use the link from your invitation email.');
            return;
        }
        if (!password || !confirmPassword) {
            setError('Please enter and confirm your password.');
            return;
        }
        if (password.length < 8) {
            setError('Password must be at least 8 characters.');
            return;
        }
        if (password !== confirmPassword) {
            setError('Passwords do not match.');
            return;
        }

        setSubmitting(true);
        try {
            const res = await axios.post(
                ACTIVATE_API_URL,
                { invitationId, email, password },
                { headers: { 'Content-Type': 'application/json' } }
            );
            const successMsg = res.data?.message
                || 'Your account has been activated successfully. You can now sign in with your new password.';
            setSuccess(successMsg);
        } catch (err) {
            const data = err.response?.data;
            const msg  = (data && (data.message || data.error)) || err.message || 'Activation failed. Please try again.';
            setError(msg);
        } finally {
            setSubmitting(false);
        }
    };

    if (success) {
        return (
            <div style={{
                background: '#ECFDF5',
                border: '1px solid #A7F3D0',
                borderRadius: 12,
                padding: '20px 18px',
                textAlign: 'center',
                marginBottom: 16,
            }}>
                <div style={{
                    width: 48, height: 48, borderRadius: '50%',
                    background: '#10B981', color: '#fff',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 24, fontWeight: 700, marginBottom: 12,
                }}>&#10003;</div>
                <h3 style={{ color: '#065F46', fontSize: 18, fontWeight: 700, margin: '0 0 6px' }}>
                    Account Activated
                </h3>
                <p style={{ color: '#047857', fontSize: 14, margin: '0 0 16px' }}>
                    {success}
                </p>
                <button
                    type="button"
                    className="login-primary-btn"
                    onClick={onActivated}
                    style={{ marginTop: 4 }}
                >
                    Go to Login
                </button>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit} noValidate>
            <div className="mb-3">
                <label htmlFor="newPassword" className="login-label">Password</label>
                <input
                    type="password"
                    className="login-input"
                    id="newPassword"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); setError(''); }}
                    autoComplete="new-password"
                    disabled={submitting}
                />
            </div>

            <div className="mb-3">
                <label htmlFor="confirmPassword" className="login-label">Confirm Password</label>
                <input
                    type="password"
                    className="login-input"
                    id="confirmPassword"
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
                    autoComplete="new-password"
                    disabled={submitting}
                />
            </div>

            {error && (
                <p style={{ color: '#DC2626', fontSize: 13, marginBottom: 12 }}>{error}</p>
            )}

            <button type="submit" className="login-primary-btn" disabled={submitting}>
                {submitting ? 'Activating…' : 'Activate Account'}
            </button>
        </form>
    );
}

export default ActiveUser;
